import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Heart, Sparkles, Truck } from "lucide-react";
import ContactSection from "../components/ContactSection";
import { benefits, testimonials, heroImages } from "../mock";
import { fetchMenu } from "../lib/api";

const iconFor = (i) => {
  const icons = [Leaf, Heart, Sparkles, Truck];
  const I = icons[i % icons.length];
  return <I size={22} />;
};

export default function HomePage() {
  const [menuItems, setMenuItems] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchMenu()
      .then((data) => {
        if (!cancelled) setMenuItems(data);
      })
      .catch(() => {
        if (!cancelled) setMenuItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingMenu(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="fade-in">
      {/* HERO */}
      <section className="relative bg-[var(--ka-cream)] overflow-hidden">
        <div className="container-ka pt-14 pb-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block text-[12px] tracking-[0.25em] uppercase text-[var(--ka-teal)] mb-5">
              Home-made · Chennai
            </span>
            <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] text-[var(--ka-teal-darker)] mb-6">
              Eat Keto.
              <br />
              Get Fit.
            </h1>
            <p className="text-[var(--ka-muted)] text-lg leading-relaxed max-w-md mb-8">
              Delicious, dietitian-designed keto meals - cooked fresh and
              delivered to your door across Chennai.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/menus" className="btn-teal">
                View Menus <ArrowRight size={16} />
              </Link>
              <Link to="/plans" className="btn-outline-teal">
                See Plans &amp; Pricing
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="hero-frame">
              <img
                src={heroImages.topBoard}
                alt="Keto food board"
                className="w-full h-[460px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-white p-5 shadow-xl rounded-md w-56">
              <div className="text-[12px] uppercase tracking-[0.18em] text-[var(--ka-teal)] mb-1">
                Net Carbs
              </div>
              <div className="font-serif text-3xl text-[var(--ka-teal-darker)]">
                &lt; 20g / day
              </div>
              <div className="text-xs text-[var(--ka-muted)] mt-1">
                Every meal calibrated
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT (dark teal) */}
      <section className="relative bg-[var(--ka-teal)] text-white overflow-hidden grain">
        <img
          src={heroImages.aboutSpread}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-luminosity"
        />
        <div className="relative container-ka section text-center max-w-3xl">
          <h2 className="font-serif text-5xl md:text-6xl mb-4">About Us</h2>
          <p className="text-white/90 text-lg mb-8">
            Experience the Best of Keto with Keto Ambrosia
          </p>
          <p className="text-white/85 leading-relaxed max-w-2xl mx-auto">
            At Keto Ambrosia, we have a passion for providing delicious and
            healthy homemade Keto foods. We started this business with one goal
            in mind — to offer our clients the best quality, homemade meals
            that are suitable for those with a keto lifestyle. We believe that
            everyone deserves to eat food that not only tastes good but is also
            good for you. We only use the freshest and most wholesome
            ingredients in all of our meals, making them delicious and
            nutritious.
          </p>
          <div className="mt-10">
            <a href="tel:+919840858201" className="btn-white">
              Contact
            </a>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="section bg-white">
        <div className="container-ka">
          <div className="text-center mb-14">
            <span className="inline-block text-[12px] tracking-[0.25em] uppercase text-[var(--ka-teal)] mb-3">
              Why Keto Ambrosia
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[var(--ka-teal-darker)]">
              The Keto lifestyle, made effortless
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <div
                key={b.title}
                className="p-7 bg-[var(--ka-cream)] rounded-md hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 rounded-full bg-[var(--ka-teal)] text-white inline-flex items-center justify-center mb-4">
                  {iconFor(i)}
                </div>
                <h3 className="font-serif text-2xl text-[var(--ka-teal-darker)] mb-2">
                  {b.title}
                </h3>
                <p className="text-sm text-[var(--ka-muted)] leading-relaxed">
                  {b.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MENU PREVIEW */}
      <section className="section bg-[var(--ka-cream)]">
        <div className="container-ka">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <span className="inline-block text-[12px] tracking-[0.25em] uppercase text-[var(--ka-teal)] mb-3">
                This week
              </span>
              <h2 className="font-serif text-4xl md:text-5xl text-[var(--ka-teal-darker)]">
                A taste of our menu
              </h2>
            </div>
            <Link
              to="/menus"
              className="text-[var(--ka-teal)] font-medium inline-flex items-center gap-1 hover:gap-2 transition-all"
            >
              View full menu <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {loadingMenu &&
              Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="menu-card overflow-hidden rounded-md animate-pulse"
                >
                  <div className="aspect-[4/3] bg-gray-200" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 w-20 bg-gray-200 rounded" />
                    <div className="h-5 w-3/4 bg-gray-200 rounded" />
                    <div className="h-4 w-full bg-gray-200 rounded" />
                  </div>
                </div>
              ))}
            {!loadingMenu &&
              menuItems.slice(0, 6).map((m) => (
              <article key={m.id} className="menu-card overflow-hidden rounded-md">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={m.image}
                    alt={m.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] tracking-[0.18em] uppercase text-[var(--ka-teal)]">
                      {m.category}
                    </span>
                    <span className="text-sm font-medium">₹{m.price}</span>
                  </div>
                  <h3 className="font-serif text-2xl text-[var(--ka-teal-darker)] mb-1">
                    {m.name}
                  </h3>
                  <p className="text-sm text-[var(--ka-muted)] mb-3">
                    {m.description}
                  </p>
                  <div className="flex gap-3 text-[11px] text-[var(--ka-muted)] border-t pt-3">
                    <span>{m.macros.kcal} kcal</span>
                    <span>F {m.macros.fat}g</span>
                    <span>P {m.macros.protein}g</span>
                    <span>NC {m.macros.net_carbs}g</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section bg-white">
        <div className="container-ka">
          <div className="text-center mb-12">
            <span className="inline-block text-[12px] tracking-[0.25em] uppercase text-[var(--ka-teal)] mb-3">
              Our customers
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[var(--ka-teal-darker)]">
              Stories from our table
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <figure
                key={t.id}
                className="p-7 border border-gray-100 rounded-md hover:shadow-lg transition-shadow bg-white"
              >
                <div className="text-[var(--ka-teal)] text-3xl font-serif leading-none mb-2">
                  &ldquo;
                </div>
                <blockquote className="text-[var(--ka-text)] text-[15px] leading-relaxed mb-5">
                  {t.quote}
                </blockquote>
                <figcaption className="text-sm">
                  <div className="font-medium text-[var(--ka-teal-darker)]">
                    {t.name}
                  </div>
                  <div className="text-[var(--ka-muted)] text-xs">
                    {t.location}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <ContactSection />
    </div>
  );
}
